(() => {

  function parseCuttingAmount(value) {
    const normalized = value.trim()
      .replace(/¼/g, ' 1/4').replace(/½/g, ' 1/2').replace(/¾/g, ' 3/4')
      .replace(/⁄/g, '/').trim();
    if (/^\d+(?:\.\d+)?$/.test(normalized) || /^\.\d+$/.test(normalized)) {
      return Number(normalized);
    }
    const fraction = normalized.match(/^(?:(\d+)\s+)?(\d+)\s*\/\s*(\d+)$/);
    if (!fraction || Number(fraction[3]) === 0) return NaN;
    return Number(fraction[1] || 0) + Number(fraction[2]) / Number(fraction[3]);
  }

  if (!customElements.get('cutting-options')) {
    customElements.define('cutting-options', class extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.input = this.querySelector('[data-cutting-input]');
        this.amount = this.querySelector('[data-cutting-amount]');
        if (!this.input || !this.amount) return;
        this.initialized = true;
        this.addEventListener('change', (event) => {
          if (event.target.name === 'properties[Cut]') this.updateAmount(true);
        });
        this.input.addEventListener('input', () => this.validateAmount());
        this.updateAmount();
      }

      updateAmount(focus = false) {
        const selected = this.querySelector('input[name="properties[Cut]"]:checked');
        const enabled = Boolean(selected?.value);
        this.amount.hidden = !enabled;
        this.input.disabled = !enabled;
        this.input.required = enabled;
        if (!enabled) this.input.value = '';
        this.validateAmount();
        if (enabled && focus) this.input.focus();
      }

      validateAmount() {
        const value = parseCuttingAmount(this.input.value);
        const invalid = !this.input.disabled && (!Number.isFinite(value) || value < 0.5 || value > 4.75);
        this.input.setCustomValidity(invalid ? this.input.dataset.error : '');
      }
    });
  }
  

  let isSyncingAddonQty = false;
  let syncQueued = false;
  let initialSyncRan = false;
  let skipNextCartUpdateSync = false;
  let loaderCount = 0;

  function normalizeValue(value) {
    return String(value ?? '').trim().toLowerCase();
  }

  function getLocaleCartUrl(path) {
    return `${window.Shopify?.routes?.root || '/'}${path}`.replace(/\/{2,}/g, '/');
  }

  function isCartPage() {
    return window.location.pathname.includes('/cart');
  }

  function getCartItemsComponents() {
    return Array.from(document.querySelectorAll('cart-items-component'));
  }

  function getSectionIds() {
    const ids = new Set();

    getCartItemsComponents().forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        ids.add(item.dataset.sectionId);
      }
    });

    return Array.from(ids);
  }

  function buildSectionsRequest() {
    return {
      sections: getSectionIds().join(','),
      sections_url: window.location.pathname
    };
  }

  async function addItems(items) {
    const response = await fetch(getLocaleCartUrl('cart/add.js'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({
        items,
        ...buildSectionsRequest()
      })
    });

    const data = await response.json();
    if (!response.ok) throw data;
    return data;
  }

  async function getCart() {
    const response = await fetch(getLocaleCartUrl('cart.js'), {
      method: 'GET',
      headers: { Accept: 'application/json' }
    });

    if (!response.ok) {
      throw new Error('Failed to fetch cart');
    }

    return response.json();
  }

  async function changeLineByKey(key, quantity) {
    const response = await fetch(getLocaleCartUrl('cart/change.js'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({
        id: key,
        quantity,
        ...buildSectionsRequest()
      })
    });

    const data = await response.json();
    if (!response.ok) throw data;
    return data;
  }

  function emitCartUpdate(cart, meta = {}) {
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: cart,
          sourceId: meta.sourceId || 'auto-addon-script',
          data: {
            didError: false,
            source: meta.source || 'cart-items-component',
            productId: meta.productId || null,
            itemCount: meta.itemCount ?? cart?.item_count ?? 0,
            variantId: meta.variantId || null,
            sections: meta.sections || null
          }
        }
      })
    );
  }

  function ensureLoaderStyles() {
    if (document.getElementById('addon-cart-loader-styles')) return;

    const style = document.createElement('style');
    style.id = 'addon-cart-loader-styles';
    style.textContent = `
      .addon-cart-loader {
        position: fixed;
        inset: 0;
        background: rgba(255,255,255,0.6);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 99999;
        opacity: 0;
        pointer-events: none;
        transition: opacity 0.2s ease;
      }

      .addon-cart-loader.is-visible {
        opacity: 1;
        pointer-events: all;
      }

      .addon-cart-loader__spinner {
        width: 42px;
        height: 42px;
        border: 3px solid rgba(0,0,0,0.15);
        border-top-color: rgba(0,0,0,0.75);
        border-radius: 50%;
        animation: addon-cart-loader-spin 0.7s linear infinite;
      }

      @keyframes addon-cart-loader-spin {
        to { transform: rotate(360deg); }
      }
    `;
    document.head.appendChild(style);
  }

  function ensureLoaderElement() {
    ensureLoaderStyles();

    let loader = document.getElementById('addon-cart-loader');
    if (loader) return loader;

    loader = document.createElement('div');
    loader.id = 'addon-cart-loader';
    loader.className = 'addon-cart-loader';
    loader.setAttribute('aria-hidden', 'true');
    loader.innerHTML = `<div class="addon-cart-loader__spinner"></div>`;
    document.body.appendChild(loader);

    return loader;
  }

  function showLoader() {
    loaderCount += 1;
    const loader = ensureLoaderElement();
    loader.classList.add('is-visible');
  }

  function hideLoader() {
    loaderCount = Math.max(0, loaderCount - 1);

    if (loaderCount > 0) return;

    const loader = document.getElementById('addon-cart-loader');
    if (loader) loader.classList.remove('is-visible');
  }

  function lineMatchesRule(lineItem, rule) {
    const props = lineItem.properties || {};
    const value = props[rule.propertyKey];

    if (typeof rule.match === 'function') {
      return rule.match(value, props);
    }

    return normalizeValue(value) === normalizeValue(rule.match);
  }

  function isAddonLine(item) {
    const props = item.properties || {};
    return String(props._auto_added || '') === 'true';
  }

  function getParentLines(cart) {
    return (cart?.items || []).filter((item) => !isAddonLine(item));
  }

  function getAddonLinesByVariant(cart, addonVariantId) {
    return (cart?.items || []).filter((item) => {
      return isAddonLine(item) && String(item.variant_id) === String(addonVariantId);
    });
  }

  function getDesiredAddonQuantity(cart, rule) {
    return getParentLines(cart).reduce((sum, line) => {
      if (lineMatchesRule(line, rule)) {
        return sum + Number(line.quantity || 0);
      }
      return sum;
    }, 0);
  }

  async function syncAddonQuantities(cart, options = {}) {
    const { suppressEvent = false } = options;

    if (isSyncingAddonQty) {
      syncQueued = true;
      return;
    }

    isSyncingAddonQty = true;
    showLoader();

    try {
      const addonAdds = [];
      const lineChanges = [];

      AUTO_ADD_RULES.forEach((rule) => {
        const desiredQty = getDesiredAddonQuantity(cart, rule);
        const addonLines = getAddonLinesByVariant(cart, rule.addonVariantId);

        if (desiredQty <= 0) {
          addonLines.forEach((line) => {
            lineChanges.push({
              key: line.key,
              quantity: 0
            });
          });
          return;
        }

        if (addonLines.length === 0) {
          addonAdds.push({
            id: Number(rule.addonVariantId),
            quantity: desiredQty,
            properties: {
              _auto_added: 'true',
              _trigger_property: rule.propertyKey
            }
          });
          return;
        }

        const keeper = addonLines[0];

        if (Number(keeper.quantity) !== Number(desiredQty)) {
          lineChanges.push({
            key: keeper.key,
            quantity: desiredQty
          });
        }

        addonLines.slice(1).forEach((extra) => {
          lineChanges.push({
            key: extra.key,
            quantity: 0
          });
        });
      });

      const hasAdds = addonAdds.length > 0;
      const hasLineChanges = lineChanges.length > 0;

      let responseWithSections = null;

      if (hasAdds) {
        responseWithSections = await addItems(addonAdds);
        cart = await getCart();
      }

      for (const change of lineChanges) {
        responseWithSections = await changeLineByKey(change.key, change.quantity);
      }

      if ((hasAdds || hasLineChanges) && !suppressEvent) {
        const freshCart = responseWithSections?.items ? responseWithSections : await getCart();

        skipNextCartUpdateSync = true;
        emitCartUpdate(freshCart, {
          sourceId: 'auto-addon-script',
          source: 'cart-items-component',
          itemCount: freshCart?.item_count || 0,
          variantId: addonAdds[0] ? String(addonAdds[0].id) : null,
          sections: responseWithSections?.sections || null
        });
      }
    } catch (error) {
      console.error('Addon sync failed:', error);
    } finally {
      isSyncingAddonQty = false;
      hideLoader();

      if (syncQueued) {
        syncQueued = false;

        try {
          const latestCart = await getCart();
          await syncAddonQuantities(latestCart, options);
        } catch (error) {
          console.error('Queued addon sync failed:', error);
        }
      }
    }
  }

  async function runInitialCartSync(force = false) {
    if (initialSyncRan && !force) return;
    initialSyncRan = true;

    try {
      const cart = await getCart();
      await syncAddonQuantities(cart);
    } catch (e) {
      console.error('Initial cart sync failed:', e);
    }
  }

  function bootInitialCartSync() {
    if (isCartPage()) {
      runInitialCartSync();
    }
  }

  function setupCartApiWatcher() {
    if (window.__addonCartApiWatcherBound) return;
    window.__addonCartApiWatcherBound = true;

    const originalFetch = window.fetch;

    window.fetch = async function (...args) {
      const response = await originalFetch.apply(this, args);

      try {
        const requestUrl =
          typeof args[0] === 'string'
            ? args[0]
            : args[0] && args[0].url
              ? args[0].url
              : '';

        const normalizedUrl = String(requestUrl);

        const isCartMutation =
          normalizedUrl.includes('/cart/add') ||
          normalizedUrl.includes('/cart/change') ||
          normalizedUrl.includes('/cart/update') ||
          normalizedUrl.includes('/cart/clear');

        if (isCartMutation && !isSyncingAddonQty) {
          setTimeout(async () => {
            try {
              const latestCart = await getCart();
              await syncAddonQuantities(latestCart);
            } catch (error) {
              console.error('Cart watcher sync failed:', error);
            }
          }, 50);
        }
      } catch (error) {
        console.error('Cart API watcher error:', error);
      }

      return response;
    };
  }

  setupCartApiWatcher();

  if (document.readyState === 'interactive' || document.readyState === 'complete') {
    bootInitialCartSync();
  }

  document.addEventListener('DOMContentLoaded', bootInitialCartSync);
  window.addEventListener('load', bootInitialCartSync);

  window.addEventListener('pageshow', () => {
    if (isCartPage()) {
      runInitialCartSync(true);
    }
  });

  document.addEventListener('cart:update', async (evt) => {
    const cart = evt.detail?.resource;
    if (!cart) return;

    if (skipNextCartUpdateSync) {
      skipNextCartUpdateSync = false;
      return;
    }

    await syncAddonQuantities(cart);
  });
})();